<i18n src="./translations"></i18n>
<template>
  <section class="pdp-footer" :class="{ desktop: isDesktop }">
    <template
      v-if="
        (contactUsInfo.phone && !contactUsInfo.hideSupportPhoneNumber) ||
        contactUsInfo.email
      "
    >
      <h5>{{ $t('pdp-footer.title') }}</h5>
      <ul class="cards" role="list">
        <li
          v-if="contactUsInfo.phone && !contactUsInfo.hideSupportPhoneNumber"
          class="card"
        >
          <address class="content">
            <a
              class="link"
              :href="`tel:${contactUsInfo.phone}`"
              :aria-label="`${$t('pdp-footer.phone-label')} ${
                contactUsInfo.formattedPhone
              }`"
              >{{ contactUsInfo.formattedPhone }}</a
            >
            <p v-if="contactUsInfo.openingTimes" class="details">
              {{ contactUsInfo.openingTimes }}
            </p>
          </address>
          <Icon name="Phone2" width="24" height="24" aria-hidden="true" />
        </li>

        <li v-if="contactUsInfo.email" class="card">
          <address class="content">
            <a
              class="link"
              :href="`mailto:${contactUsInfo.email}`"
              :aria-label="`${$t('pdp-footer.email-label')} ${
                contactUsInfo.email
              }`"
            >
              {{ contactUsInfo.email }}</a
            >
            <p class="details">{{ $t('pdp-footer.email-details') }}</p>
          </address>
          <Icon name="Email2" width="24" height="24" aria-hidden="true" />
        </li>
      </ul>
    </template>

    <FooterServices
      v-if="isDesktop && !!paymentMethods.length"
      class="payment-methods"
      type="payment"
      :providers="paymentMethods"
    />
  </section>
</template>

<script>
import Icon from 'Components/00-generated/Icon';
import { mapState, mapGetters } from 'vuex';
import FooterServices from 'Components/02-molecules/footer-services/FooterServices';

export default {
  name: 'PdpFooter',
  components: {
    Icon,
    FooterServices,
  },
  props: {
    isDesktop: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapState('core', {
      freeDeliveryMin: ({ freeDeliveryMin }) => freeDeliveryMin,
      currency: ({ currency }) => currency,
      salesChannelConfig: ({ salesChannel }) => salesChannel.config,
      salesChannelId: ({ salesChannel }) => salesChannel.salesChannelId,
      paymentMethods({ paymentMethods }) {
        const methods = paymentMethods.map((icon) => ({
          icon,
          title: this.$t(`pdp-footer.payment.${icon}`),
        }));

        return this.isBusinessCustomer ? [methods[0]] : methods;
      },
    }),
    ...mapGetters('core', ['isBusinessCustomer']),
    contactUsInfo() {
      return {
        email: this.isBusinessCustomer
          ? this.salesChannelConfig.b2BCustomerServiceEmail
          : this.salesChannelConfig.customerServiceEmail,
        phone: this.isBusinessCustomer
          ? this.salesChannelConfig.b2BCustomerServicePhoneLink
          : this.salesChannelConfig.customerServicePhoneLink,
        formattedPhone: this.isBusinessCustomer
          ? this.salesChannelConfig.b2BCustomerServicePhoneNumber
          : this.salesChannelConfig.customerServicePhoneNumber,
        openingTimes: this.isBusinessCustomer
          ? this.salesChannelConfig.b2BCustomerServiceOpeningHours
          : this.salesChannelConfig.customerServiceOpeningHours,
        hideSupportPhoneNumber:
          this.salesChannelConfig.hideSupportPhoneNumber || false,
      };
    },
  },
};
</script>

<style scoped lang="scss">
@import 'variables/breakpoints';
@import 'mixins';

.pdp-footer {
  h5 {
    font-size: var(--font-size-SM);
    margin-top: var(--space-3);
    margin-bottom: var(--space-1--half);
  }

  .cards {
    display: flex;
    gap: var(--space-1--half);
    flex-direction: column;

    .card {
      display: flex;
      align-items: center;
      flex: 1;
      padding: var(--space-2);
      border-radius: 3px;
      border: 1px solid var(--color-kfzteile24-gray);
      background: var(--color-rebranding-alabaster);
      line-height: 18px;

      .link {
        color: var(--color-kfzteile24-secondary);
        font-size: var(--font-size-SM);
        font-weight: 700;
      }

      .details {
        color: var(--color-charcoal-gray);
        font-size: var(--font-size-S);
        font-weight: 400;
      }

      .icon {
        margin-left: auto;
      }
    }
  }

  .payment-methods {
    // Exception, should be moved to FooterServices component later
    ::v-deep .items {
      display: flex;
      justify-content: center;
      align-items: center;

      .item {
        width: 66px;
        border: 1px solid var(--color-kfzteile24-gray);
      }
    }
  }

  &.desktop {
    .cards {
      flex-direction: row;

      .card {
        flex-direction: column-reverse;
        justify-content: center;
        align-items: center;

        .content {
          text-align: center;
        }

        .icon {
          margin-left: 0;
          margin-bottom: var(--space-1);
        }
      }
    }

    @media #{$_mediaMDown} {
      display: none;
    }
  }
}
</style>
